// PR 58 / 59 fixes · F2 — "First-year engagement - no comparatives" on Engagement settings (EN + AR, strings in
// lib/i18n/1580_fx_b.js). Saved on its own route (PUT /engagements/{id}/first-year {value}); the server is the rule: Partner and the
// manager roles only, refused on a locked engagement, refused ON (400 FIRST_YEAR_HAS_PRIOR_DATA) when prior-year data exists —
// the refusal is shown under the toggle. Other roles see the setting read-only. A missing field reads "off".
// SUNDAY BATCH · FX2-2: the state route (GET /engagements/{id}/first-year) says whether prior-year data exists; with the flag
// ON and prior-year data loaded (after the flag was set) the red warning "Prior-year data exists - clear the first-year flag"
// shows under the toggle (FirstYearConflictNote, EN + AR from the server).
// SUNDAY BATCH · FX2-3: ON only for an engagement SAVED as "Initial engagement" (ISA 510). The state route's turn_on_refusal
// (the server's rule, EN + AR) disables the switch while it is off and shows the reason in red ("save the engagement as Initial
// first" — the dialog's unsaved Initial / Continuing choice does not count until it is saved). An engagement flagged before the
// rule while continuing keeps the switch enabled so it can be turned OFF, with the same red reason.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import FirstYearConflictNote from "@/components/FirstYearConflictNote";

export const FIRST_YEAR_FLAG = "first_year_no_comparatives";
// the server's first_year.SET_ROLES
export const FIRST_YEAR_SET_ROLES = new Set(["Partner", "Audit Manager", "Manager", "Senior Manager", "Assistant Manager"]);

export function refusalText(err, lang, fallback) {
  const d = err?.response?.data || {};
  if (lang === "ar" && d.detail_ar) return d.detail_ar;
  return (typeof d.detail === "string" && d.detail) || fallback;
}

export default function FirstYearToggle({ engagement, locked = false, onChanged }) {
  const { t, lang } = useI18n();
  const { user } = useAuth() || {};
  const [value, setValue] = useState(engagement?.[FIRST_YEAR_FLAG] === true);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState("");
  const [state, setState] = useState(null);      // FX2-2: GET /engagements/{id}/first-year
  useEffect(() => { setValue(engagement?.[FIRST_YEAR_FLAG] === true); setRefusal(""); }, [engagement]);
  const engId = engagement?.id;
  useEffect(() => {
    if (!engId) return undefined;
    let alive = true;
    api.get(`/engagements/${engId}/first-year`, { _silent: true })
      .then((r) => { if (alive) setState(r?.data && !Array.isArray(r.data) ? r.data : null); })
      .catch(() => { if (alive) setState(null); });
    return () => { alive = false; };
  }, [engId, engagement]);
  if (!engagement) return null;
  const canSet = FIRST_YEAR_SET_ROLES.has(user?.role);
  const notInitial = state?.turn_on_refusal || null;          // FX2-3
  const blockedOn = !value && !!notInitial;
  const change = async (next) => {
    setBusy(true);
    setRefusal("");
    try {
      await api.put(`/engagements/${engagement.id}/first-year`, { value: next }, { _silent: true });
      setValue(next);
      toast.success(t("fxB.firstYear.saved"));
      if (onChanged) onChanged(next);
    } catch (e) {
      const msg = refusalText(e, lang, t("fxB.firstYear.saveFailed"));
      setRefusal(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30"
         data-testid="eng-settings-first-year">
      {canSet ? (
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={value} disabled={busy || locked || blockedOn}
                 onChange={(e) => change(e.target.checked)} data-testid="eng-settings-first-year-checkbox" />
          <div className="flex-1">
            <div className="text-sm font-semibold">{t("fxB.firstYear.label")}</div>
            <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{t("fxB.firstYear.help")}</div>
          </div>
        </label>
      ) : (
        <div data-testid="eng-settings-first-year-readonly">
          <div className="text-sm font-semibold">{t("fxB.firstYear.label")}</div>
          <div className="mt-1 text-xs" data-testid="eng-settings-first-year-value">
            {value ? t("fxB.firstYear.on") : t("fxB.firstYear.off")}
          </div>
          <div className="mt-1 text-[11px] italic text-muted-foreground">{t("fxB.firstYear.rolesOnly")}</div>
        </div>
      )}
      {notInitial && (canSet || value) && (
        <FirstYearConflictNote conflict={notInitial} testId="eng-settings-first-year-not-initial" className="mt-2" />
      )}
      {value && state?.prior_data_exists && (
        <FirstYearConflictNote conflict={state.conflict} testId="eng-settings-first-year-conflict" className="mt-2" />
      )}
      {refusal && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
             role="alert" data-testid="eng-settings-first-year-refusal">{refusal}</div>
      )}
    </div>
  );
}
